import axios, { AxiosInstance, CreateAxiosDefaults } from 'axios';
import qs from 'qs';
import { getCorrectLang } from 'src/utils/formatTime';
import { PATH_WELCOME_PAGE } from 'src/routes/paths';

const BASE_API_PATH = import.meta.env.DEV
  ? '/api'
  : `${import.meta.env.VITE_HOST_API_BASEURL}/api`;

const VERSION = import.meta.env.VITE_HOST_API_VERSION;
const LANG = getCorrectLang();

const COOKIE_ENV_SUFFIX = import.meta.env.VITE_COOKIE_ENV_SUFFIX ?? '';
const XSRF_COOKIE_NAME = `XSRF-TOKEN-C${COOKIE_ENV_SUFFIX}`;

const errorInterceptor = (error: any) => {
  const fallback = error || 'Something went wrong';
  return Promise.reject(fallback);
};

// Array params serialized as ?id=1&id=2 instead of axios default ?id[]=1&id[]=2
const repeatArraySerializer = {
  paramsSerializer: {
    serialize: (params: any) => qs.stringify(params, { arrayFormat: 'repeat' }),
  },
};

const CSRF_METHODS = ['post', 'put', 'patch', 'delete'];

// XSRF token cache — populated by response interceptor from X-XSRF-TOKEN-C response header.
// Required because the XSRF-TOKEN-C cookie has Path=/api and is not visible to document.cookie
// on SPA pages (/auth/*, /dashboard/*). The definitive fix is BE setting Path=/ on the cookie.
let _xsrfTokenCache: string | undefined;

export const setXsrfTokenCache = (token: string) => { _xsrfTokenCache = token; };

// In-memory token expiry (epoch seconds). Populated from X-Token-Exp response header.
// Used by the proactive refresh interceptor to avoid sending requests with an expired token.
let _tokenExp: number | undefined;

export const setTokenExp = (exp: number) => { _tokenExp = exp; };
export const getTokenExp = () => _tokenExp;


let _impersonationExp: number | undefined;

export const setImpersonationExp = (exp: number | undefined) => { _impersonationExp = exp; };
export const getImpersonationExp = () => _impersonationExp;

const getXsrfToken = (): string | undefined => {
  // 1. In-memory cache (populated from response header X-XSRF-TOKEN-C by interceptor below)
  if (_xsrfTokenCache) return _xsrfTokenCache;
  // 2. Fallback: direct cookie read — works only when BE sets Path=/ on XSRF-TOKEN-C
  const prefix = `${XSRF_COOKIE_NAME}=`;
  const row = document.cookie.split('; ').find((r) => r.startsWith(prefix));
  return row?.substring(prefix.length);
};

/**
 * Refreshes the access token and, critically, re-syncs the in-memory XSRF cache.
 *
 * Why this exists: /auth/refresh rotates the underlying auth/session identity,
 * which can invalidate the binding of any XSRF token issued before the rotation
 * (the BE binds the antiforgery token to the request's NameIdentifier — see BE
 * EmitXsrfCookie). /me does NOT (re)generate the XSRF cookie — only /xsrf does.
 * Without this explicit call, the FE keeps using a stale cached XSRF token after
 * every refresh, and the next mutating request (POST/PUT/PATCH/DELETE) fails with
 * CSRF_VALIDATION_FAILED. This is a "no-regret" call even if the BE turns out to
 * keep the binding stable across refresh: it costs one cheap extra request inside
 * a flow that already makes two (/refresh + /me).
 */
const refreshSessionAndXsrf = async (): Promise<void> => {
  await blimpAxiosClient.auth.post('/refresh');
  const { data: me } = await blimpAxiosClient.auth.get('/me');

  const wasImpersonating = _impersonationExp !== undefined;
  const isImpersonating = !!me.originalUser;

  setTokenExp(me.originalUser?.exp ?? me.user?.exp ?? 0);
  setImpersonationExp(isImpersonating ? me.user?.exp : undefined);

  if (wasImpersonating && !isImpersonating) {
    window.location.replace(PATH_WELCOME_PAGE);
    return new Promise<never>(() => {});
  }

  await blimpAxiosClient.auth.get('/xsrf');
};
// Impersonation cookie has its own fixed lifetime and is NOT renewed by /refresh,
// so it can die silently mid-session while the base session stays valid. This check
// closes that gap: a free local comparison, a real GET /me only when it's actually
// due, deduped across concurrent triggers.
let _impersonationCheckInFlight: Promise<void> | null = null;

export const checkImpersonationExpiry = (): Promise<void> => {
  if (_impersonationExp === undefined || Date.now() / 1000 <= _impersonationExp) {
    return Promise.resolve();
  }
  if (_impersonationCheckInFlight) return _impersonationCheckInFlight;

  _impersonationCheckInFlight = blimpAxiosClient.auth
    .get('/me')
    .then(({ data: me }) => {
      if (!me.originalUser) {
        // Impersonation really is over — FE state doesn't know yet.
        setImpersonationExp(undefined);
        window.location.replace(PATH_WELCOME_PAGE);
      } else {
        setImpersonationExp(me.user.exp);
      }
    })
    .catch(() => {
      // Base session is dead too — the 401 handler below will catch the next real call.
    })
    .finally(() => {
      _impersonationCheckInFlight = null;
    });

  return _impersonationCheckInFlight;
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkImpersonationExpiry();
    }
  });
}
// END NEW ──────────────────────────────────────────────────────────────────────

export interface CustomAxiosInstance extends AxiosInstance {
  // Returns fresh axios instance: same baseURL, Authorization stripped, withCredentials: false.
  // Use for unauthenticated sub-paths (e.g. /auth/realm) — NOT for external absolute URLs.
  withNoAuth(): AxiosInstance;
}

export interface BlimpClient {
  // /api/auth/* — withCredentials always on; cookie-based auth
  auth: CustomAxiosInstance;
  // /api/v{VERSION}/{lang}/* — cookie-based auth
  api: CustomAxiosInstance;
}

/**
 * Creates a custom axios instance with XSRF protection and a no-auth escape hatch.
 * Axios built-in XSRF is disabled (xsrfCookieName/xsrfHeaderName set to '') to prevent
 * it from sending the old default X-XSRF-TOKEN header alongside our X-XSRF-TOKEN-C.
 * CSRF is handled exclusively by the request interceptor below.
 */
const createCustomInstance = (
  creationConfig: CreateAxiosDefaults
): CustomAxiosInstance => {
  const instance = axios.create({
    ...creationConfig,
    xsrfCookieName: '',  // disable axios built-in XSRF — handled by interceptor
    xsrfHeaderName: '',
  }) as CustomAxiosInstance;

  instance.interceptors.request.use((config) => {
    const url = config.url ?? '';

    if (CSRF_METHODS.includes(config.method ?? '')) {
      const token = getXsrfToken();
      // Only attach when the cookie exists — before login it won't be present
      if (token) config.headers['X-XSRF-TOKEN-C'] = token;
    }
    return config;
  });

  instance.interceptors.response.use((r) => {
    // Cache XSRF token — workaround for XSRF-TOKEN-C Path=/api not visible to document.cookie
    const xsrf = r.headers['x-xsrf-token-c'];
    if (xsrf) _xsrfTokenCache = xsrf;

    return r;
  }, errorInterceptor);

  instance.withNoAuth = (): AxiosInstance => {
    // axios.create (not Object.create) → independent interceptor stack → XSRF does NOT fire.
    // withCredentials: false → no cookies sent to services that don't expect them.
    const noAuth = axios.create({ ...creationConfig, withCredentials: false });
    delete (noAuth.defaults.headers.common as Record<string, unknown>)['Authorization'];
    noAuth.interceptors.response.use((r) => r, errorInterceptor);
    return noAuth;
  };

  return instance;
};

const createBlimpClient = (): BlimpClient => ({
  auth: createCustomInstance({
    baseURL: `${BASE_API_PATH}/auth`,
    withCredentials: true
  }),

  api: createCustomInstance({
    baseURL: `${BASE_API_PATH}/v${VERSION}/${LANG}`,
    ...repeatArraySerializer,
    withCredentials: true
  }),
});

export const blimpAxiosClient: BlimpClient = createBlimpClient();

// ─── Proactive refresh on blimpAxiosClient.api requests ──────────────────────
//
// If the access token is expired or expiring within 60 s, refresh before sending
// the request so it never reaches the server with an expired token → no 401s.
// Also re-syncs the XSRF cache (see refreshSessionAndXsrf) so mutating requests
// queued behind the refresh don't fire with a now-stale XSRF token.
// Shares _isRefreshing / _refreshQueue with the 401 response interceptor below.

let _isRefreshing = false;
let _refreshQueue: Array<() => void> = [];

// Separate lock for CSRF-token-only refresh (GET /xsrf), distinct from _isRefreshing
// (which guards the full session refresh /refresh + /me). Without this, N concurrent
// CSRF_VALIDATION_FAILED responses each call /xsrf independently; the BE issues a new
// XSRF-TOKEN-C/-S cookie pair PER CALL, and each new pair invalidates the previous one
// server-side. The last response to land "wins" — everyone else's retry uses an
// already-superseded token and fails again. Observed directly: 5 parallel failed
// requests → 5 distinct Set-Cookie pairs → only the very last one is valid.
let _isCsrfRefreshing = false;
let _csrfRefreshPromise: Promise<void> | null = null;

const ensureFreshXsrf = (): Promise<void> => {
  if (_isCsrfRefreshing && _csrfRefreshPromise) return _csrfRefreshPromise;
  _isCsrfRefreshing = true;
  _csrfRefreshPromise = blimpAxiosClient.auth
    .get('/xsrf')
    .then(() => undefined)
    .finally(() => {
      _isCsrfRefreshing = false;
      _csrfRefreshPromise = null;
    });
  return _csrfRefreshPromise;
};

blimpAxiosClient.api.interceptors.request.use(async (config) => {
  await checkImpersonationExpiry();

  if (_tokenExp !== undefined && Date.now() / 1000 > _tokenExp - 60) {
    if (_isRefreshing) {
      await new Promise<void>((resolve) => _refreshQueue.push(resolve));
    } else {
      _isRefreshing = true;
      try {
        await refreshSessionAndXsrf();
        _refreshQueue.forEach((cb) => cb());
        _refreshQueue = [];
      } catch {
        _refreshQueue = [];
        window.location.href = '/auth/login';
        return Promise.reject(new Error('Session expired'));
      } finally {
        _isRefreshing = false;
      }
    }
  }
  return config;
});

// ─── 401 interceptor on blimpAxiosClient.api only ────────────────────────────
//
// When the access cookie expires the API returns 401. This interceptor:
//   1. Calls /auth/refresh + /auth/me + /auth/xsrf (via refreshSessionAndXsrf, which
//      uses blimpAxiosClient.auth — no 401 interceptor there, so a failed refresh
//      cannot trigger an infinite loop).
//   2. Retries the original request once the new cookie/XSRF token are set.
//   3. If refresh itself fails, redirects to login.
//
// Concurrent 401s are queued so only one refresh cycle is run.

blimpAxiosClient.api.interceptors.response.use(
  (r) => r,
  async (error) => {
    // ── CSRF mismatch: invisible retry-once ──────────────────────────────────
    //
    // Root cause is not fully pinned down (candidates: stale in-memory XSRF
    // cache after token refresh/impersonation, or BE-side antiforgery token
    // invalidation e.g. Data Protection key rotation across instances). Rather
    // than chase every possible cause, we recover transparently regardless of
    // *why* the mismatch happened: fetch a fresh XSRF token and replay the
    // exact request that failed. Only if the retried request ALSO fails do we
    // fall back to the visible error page — at that point a token refresh is
    // very unlikely to be the issue (e.g. truly dead session, BE outage).
    //
    // _csrfRetried guards against an infinite loop if the retried request
    // fails with CSRF_VALIDATION_FAILED again.
    if (error?.response?.data?.error === 'CSRF_VALIDATION_FAILED') {
      if (!error.config || error.config._csrfRetried) {
        window.location.href = '/auth/refresh-error';
        return Promise.reject(error);
      }
      try {
        // Shared/awaited across all concurrently-failed requests — see ensureFreshXsrf.
        await ensureFreshXsrf();
        error.config._csrfRetried = true;
        return blimpAxiosClient.api.request(error.config);
      } catch {
        window.location.href = '/auth/refresh-error';
        return Promise.reject(error);
      }
    }

    // Only intercept 401 responses that have a retryable config
    if (error?.response?.status !== 401 || !error.config) return Promise.reject(error);
    if (_isRefreshing) {
      // Park this request until the in-flight refresh resolves
      return new Promise((resolve) =>
        _refreshQueue.push(() => resolve(blimpAxiosClient.api.request(error.config))),
      );
    }

    _isRefreshing = true;
    try {
      await refreshSessionAndXsrf();
      _refreshQueue.forEach((cb) => cb());
      _refreshQueue = [];
      return blimpAxiosClient.api.request(error.config);
    } catch {
      // Refresh cookie is also expired — session is dead
      window.location.href = '/auth/login';
      return Promise.reject(error);
    } finally {
      _isRefreshing = false;
      _refreshQueue = [];
    }
  },
);

// ─── Plain HTTP client ────────────────────────────────────────────────────────
// For absolute external URLs (Azure SAS, third-party APIs, etc.).
// No baseURL, no auth headers, no interceptors — raw axios.
export const httpAxiosClient: AxiosInstance = axios.create();

// Deprecated aliases — import blimpAxiosClient directly in new code
export const axiosAuthInstance: CustomAxiosInstance = blimpAxiosClient.auth;
export const axiosApiInstance: CustomAxiosInstance = blimpAxiosClient.api;